// COMP-DOC-VIEWER — نمایشگر داخلی سند (AC-DOC-004.1): تصویر و PDF با بزرگ‌نمایی و چرخش؛ فایل با نشانی کوتاه‌عمر (۵ دقیقه، توکن یک‌بارمصرف
// ثبت‌شده در سرور — C-AGENT-496) دریافت و به‌صورت Object URL محلی نمایش داده می‌شود. اسناد Clinical/HighlySensitive واترمارک نام کاربر و زمان دارند
// (متن واترمارک از سرور). PDF با نمایشگر داخلی مرورگر نمایش داده می‌شود (بدون کتابخانه pdf.js — C-AGENT-508).
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type DocFileUrlView, type PhoenixClient } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, LoadingState } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { COMP_DOC_VIEWER } from './common';

/** دریافت فایل از نشانی کوتاه‌عمر (توکن یک‌بارمصرف در مسیر؛ بدون احراز هویت). */
export async function fetchFile(api: PhoenixClient, url: string): Promise<Blob> {
  const token = url.slice(url.lastIndexOf('/') + 1);
  const result = await api.GET('/api/v1/documents/files/{token}', { params: { path: { token } }, parseAs: 'blob' });
  if (!result.response.ok) {
    throw (result.error as unknown) ?? new Error('file');
  }
  return result.data as unknown as Blob;
}

/** Object URL محلی برای Blob (آزادسازی هنگام تغییر). */
export function useObjectUrl(blob: Blob | undefined | null): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob || typeof URL.createObjectURL !== 'function') {
      setUrl(undefined);
      return undefined;
    }
    const created = URL.createObjectURL(blob);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [blob]);
  return url;
}

export interface FileViewProps {
  blob: Blob | undefined;
  contentType: string;
  title: string;
  watermark?: string | null;
}

/** نمایش Blob با بزرگ‌نمایی، چرخش و واترمارک. */
export function FileView({ blob, contentType, title, watermark }: FileViewProps) {
  const { t } = useTranslation('doc');
  const url = useObjectUrl(blob);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const isImage = contentType.startsWith('image/');
  const isPdf = contentType === 'application/pdf';

  return (
    <Stack spacing={1} data-testid="doc-viewer" data-content-type={contentType}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Tooltip title={t('doc:viewer.zoom_in')}>
          <IconButton aria-label={t('doc:viewer.zoom_in')} onClick={() => setZoom((value) => Math.min(4, value + 0.25))} data-testid="doc-viewer-zoom-in">
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('doc:viewer.zoom_out')}>
          <IconButton aria-label={t('doc:viewer.zoom_out')} onClick={() => setZoom((value) => Math.max(0.5, value - 0.25))} data-testid="doc-viewer-zoom-out">
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('doc:viewer.rotate')}>
          <IconButton aria-label={t('doc:viewer.rotate')} onClick={() => setRotation((value) => (value + 90) % 360)} data-testid="doc-viewer-rotate">
            <RotateLeftIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" data-testid="doc-viewer-state" data-zoom={String(zoom)} data-rotation={String(rotation)}>
          {t('doc:viewer.zoom', { value: faDigits(Math.round(zoom * 100)) })}
        </Typography>
      </Stack>
      <Box sx={{ position: 'relative', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, height: '65vh', bgcolor: 'grey.100' }}>
        <Box sx={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transformOrigin: 'center top', transition: 'transform 0.15s', height: '100%' }}>
          {!url ? null : isImage ? (
            <Box component="img" src={url} alt={title} sx={{ maxWidth: '100%', display: 'block', mx: 'auto' }} />
          ) : isPdf ? (
            <Box component="iframe" src={url} title={title} sx={{ width: '100%', height: '100%', border: 0 }} />
          ) : (
            <Typography sx={{ p: 2 }}>{t('doc:viewer.no_preview')}</Typography>
          )}
        </Box>
        {watermark ? (
          <Box
            aria-hidden="true"
            data-testid="doc-viewer-watermark"
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(200, 0, 0, 0.22)',
              fontSize: 28,
              fontWeight: 700,
              transform: 'rotate(-30deg)',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            {watermark}
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
}

export interface DocumentViewerProps {
  documentId: string;
  title: string;
  version?: number;
  onClose: () => void;
}

/** گفتگوی پیش‌نمایش سند ثبت‌شده. */
export function DocumentViewer({ documentId, title, version, onClose }: DocumentViewerProps) {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const preview = useQuery({
    queryKey: ['documents', 'preview', documentId, version],
    queryFn: async (): Promise<{ view: DocFileUrlView; blob: Blob }> => {
      const view = await unwrap(
        tenantApi.POST('/api/v1/documents/documents/{id}:preview-url', {
          params: { path: { id: documentId }, query: version ? { version } : {} },
        }),
      );
      return { view, blob: await fetchFile(tenantApi, view.url) };
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="doc-viewer-title" data-screen-id={COMP_DOC_VIEWER}>
      <DialogTitle id="doc-viewer-title">{title}</DialogTitle>
      <DialogContent>
        {preview.isPending ? <LoadingState rows={3} /> : null}
        {preview.error ? <ErrorState error={preview.error} onRetry={() => void preview.refetch()} /> : null}
        {preview.data ? (
          <FileView blob={preview.data.blob} contentType={preview.data.view.contentType} title={title} watermark={preview.data.view.watermark} />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="doc-viewer-close">
          {t('doc:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
